<script setup lang="ts">
  import { createReusableTemplate } from '@vueuse/core'
  import { type FunctionalComponent, type SVGAttributes } from 'vue'
  import IconAd from '~icons/te/ad'
  import IconBackground from '~icons/te/background'
  import IconDisable from '~icons/te/disable'
  import IconLink from '~icons/te/link'
  import IconPriority from '~icons/te/priority'
  import { useAuth } from '~renderer/composables/useAuth'
  import { isAnonymousUser } from '~shared/util/user'

  const { user, accounts, signIn } = useAuth()

  const [DefineSupportPerkIcon, SupportPerkIcon] = createReusableTemplate<{
    icon: FunctionalComponent<SVGAttributes>
    text: string
  }>()
</script>

<template>
  <DefineSupportPerkIcon v-slot="{ icon, text }">
    <div class="flex flex-row items-center gap-3 md:flex-col">
      <Component :is="icon" class="size-6 text-base" />
      <span>{{ text }}</span>
    </div>
  </DefineSupportPerkIcon>
  <div
    class="corner-squircle relative flex flex-col items-center justify-center gap-8 overflow-hidden rounded-4xl border border-white/5 p-8 text-center text-balance shadow-2xl backdrop-blur-sm md:p-10"
  >
    <!-- Background decoration with Waves -->
    <div class="absolute inset-0 -z-10 bg-linear-to-br from-[#134365] to-[#134365]">
      <div
        style="background-image: url('/images/wave1.svg')"
        class="motion-safe:float-1 absolute inset-0 -bottom-4 bg-bottom bg-repeat-x opacity-2"
      ></div>
      <div
        style="background-image: url('/images/wave2.svg')"
        class="motion-safe:float-2 absolute inset-0 bg-bottom bg-repeat-x opacity-3"
      ></div>
      <div
        style="background-image: url('/images/wave1.svg')"
        class="motion-safe:float-3 absolute inset-0 bg-bottom bg-repeat-x opacity-5"
      ></div>
      <div
        style="background-image: url('/images/wave2.svg')"
        class="motion-safe:float-4 absolute inset-0 bg-bottom bg-repeat-x opacity-5"
      ></div>
      <div class="absolute inset-0 bg-radial from-50% to-black/50"></div>
    </div>

    <div class="flex flex-col items-center justify-center gap-2">
      <h2 class="font-display text-3xl font-bold text-white">Support the Community</h2>
      <p class="max-w-2xl text-sm text-white md:text-base">
        Help Turnip Exchange continue to grow by supporting us on Patreon. In return for exclusive
        perks!
      </p>
    </div>

    <!-- Perks Icons -->
    <div
      class="grid gap-2 text-sm font-medium not-md:grid-rows-5 md:grid-cols-[repeat(5,8rem)]"
      :class="{
        'mb-8 flex flex-col justify-center gap-4 text-sm font-medium text-white md:flex-row md:flex-wrap md:gap-8': false,
      }"
    >
      <SupportPerkIcon :icon="IconAd" text="No Ads" />
      <SupportPerkIcon :icon="IconPriority" text="Priority Listing" />
      <SupportPerkIcon :icon="IconDisable" text="No Max Restrictions" />
      <SupportPerkIcon :icon="IconBackground" text="Custom Backgrounds" />
      <SupportPerkIcon :icon="IconLink" text="Custom URLs" />
    </div>

    <div class="flex flex-wrap justify-center gap-4">
      <a
        class="cursor-pointer rounded-lg border-2 border-blue-400 bg-transparent px-3 py-3 font-bold text-blue-100 transition-all hover:text-blue-400"
        href="https://patreon.warp.world/"
        target="_blank"
      >
        View Patreon Tiers
      </a>
      <button
        class="flex cursor-pointer items-center rounded-lg bg-blue-400 px-3 py-3 font-bold text-blue-950 shadow-lg shadow-blue-500/20 transition-all hover:text-white"
        @click="signIn('patreon')"
        v-if="user && !isAnonymousUser(user) && !accounts.patreon"
      >
        Link Your Patreon
      </button>
    </div>
  </div>
</template>
